import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from './Button';

/**
 * Empty state.
 *
 * The brief's three questions, in order: what is empty, why is it empty, what
 * can I do next. Most empty states in this app answered only the first —
 * "No submissions yet" with an icon and nothing else.
 *
 * The `filtered` variant matters: an empty list because you filtered it is a
 * completely different situation from an empty list because nothing exists,
 * and conflating them is how people conclude their data is gone.
 *
 *   <EmptyState
 *     icon="FileText"
 *     title="No submissions yet"
 *     body="Papers appear here once authors submit to this conference."
 *     action={{ label: 'Browse conferences', onClick: go }}
 *   />
 *
 *   <EmptyState filtered onClear={reset} />
 */
export default function EmptyState({
  icon = 'Inbox',
  title,
  body,
  action,
  secondaryAction,
  filtered = false,
  onClear,
  className = '',
  compact = false,
}) {
  const { t } = useTranslation('common');
  const resolvedIcon = filtered ? 'SearchX' : icon;
  const resolvedTitle =
    title ??
    (filtered
      ? t('emptyState.noMatches', 'No matches')
      : t('emptyState.nothingYet', 'Nothing here yet'));
  const resolvedBody =
    body ??
    (filtered
      ? t(
          'emptyState.filteredBody',
          'No items match the filters you have applied. Try widening them or clearing them entirely.'
        )
      : undefined);

  return (
    <div className={`text-center ${compact ? 'py-8 px-4' : 'py-14 px-6'} ${className}`.trim()}>
      <div
        className={`${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-lg bg-muted flex items-center justify-center mx-auto mb-3`}
      >
        <Icon name={resolvedIcon} size={compact ? 17 : 20} className="text-muted-foreground" />
      </div>

      <h3 className="text-sm font-semibold text-foreground">{resolvedTitle}</h3>

      {resolvedBody && (
        <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto leading-relaxed">
          {resolvedBody}
        </p>
      )}

      {(action || (filtered && onClear) || secondaryAction) && (
        <div className="mt-5 flex items-center justify-center gap-2">
          {filtered && onClear && (
            <Button variant="outline" size="sm" onClick={onClear} iconName="X" iconPosition="left">
              {t('actions.clearFilters', 'Clear filters')}
            </Button>
          )}
          {action && (
            <Button
              variant="default"
              size="sm"
              onClick={action.onClick}
              iconName={action.icon}
              iconPosition="left"
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
